/**
 * Designed case folio — HTML is the bilingual layout SoT.
 * PDF is a print companion (Helvetica). No invented GPS / close / arrival.
 */

function clip(value, limit = 128) {
  return String(value == null ? "" : value).trim().slice(0, limit);
}

function esc(text) {
  return String(text == null ? "" : text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function dash(value, empty = "—") {
  const t = clip(value, 160);
  return t || empty;
}

function yesNo(flag, yes, no) {
  return flag ? yes : no;
}

function secondsLabel(n) {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  const s = Math.max(0, Math.floor(Number(n)));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
}

function asLl(raw) {
  if (!Array.isArray(raw) || raw.length < 2) return null;
  const lng = Number(raw[0]);
  const lat = Number(raw[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  if (Math.abs(lat) <= 1e-9 && Math.abs(lng) <= 1e-9) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return [lng, lat];
}

export function cleanDossier(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const place = src.place && typeof src.place === "object" ? src.place : {};
  const ai = src.ai && typeof src.ai === "object" ? src.ai : {};
  const human = src.human && typeof src.human === "object" ? src.human : {};
  const media = src.media && typeof src.media === "object" ? src.media : {};
  const loop = src.loop && typeof src.loop === "object" ? src.loop : {};
  const ll = asLl(place.ll);
  const dispatches = Array.isArray(src.dispatches)
    ? src.dispatches.slice(0, 16).map((row) => ({
        dispatch_id: clip(row && row.dispatch_id, 80),
        status: clip(row && row.status, 32),
        target: clip(row && row.target, 80),
        site_id: clip(row && row.site_id, 64),
        device_id: clip(row && row.device_id, 80),
        dispatched_at: clip(row && row.dispatched_at, 40),
        accepted_at: clip(row && row.accepted_at, 40),
        en_route_at: clip(row && row.en_route_at, 40),
        on_scene_at: clip(row && row.on_scene_at, 40),
        responded: !!(row && row.responded),
        response_seconds: row && row.response_seconds != null ? Number(row.response_seconds) : null,
        on_scene_marked: !!(row && row.on_scene_marked),
        arrived: false,
        arrival_proof: !!(row && row.arrival_proof),
        soft_invent: false,
      })).filter((row) => row.dispatch_id)
    : [];
  return {
    event_id: clip(src.event_id, 80),
    team_run_id: clip(src.team_run_id, 64),
    opened_at: clip(src.opened_at, 40),
    device_id: clip(src.device_id || place.device_id, 80),
    place: {
      address: clip(place.address, 160),
      site_id: clip(place.site_id, 64),
      zone_id: clip(place.zone_id, 64),
      device_id: clip(place.device_id, 80),
      ll,
      ll_source: clip(place.ll_source, 24),
      map_available: !!(place.map_available && ll),
      skip_reason: clip(place.skip_reason, 48),
      soft_invent: false,
    },
    dispatches,
    ai: {
      intervened: !!ai.intervened,
      live_brain: !!ai.live_brain,
      brain_honesty: clip(ai.brain_honesty, 24),
      intents_source: clip(ai.intents_source, 24),
      authority_state: clip(ai.authority_state, 32),
      effective_model_id: clip(ai.effective_model_id, 64),
      via: clip(ai.via, 32),
      model_caps: Array.isArray(ai.model_caps) ? ai.model_caps.map((x) => clip(x, 48)).filter(Boolean).slice(0, 8) : [],
      soft_invent: false,
    },
    human: {
      intervened: !!human.intervened,
      last_control_verb: clip(human.last_control_verb, 32),
      verbs: Array.isArray(human.verbs) ? human.verbs.map((x) => clip(x, 32)).filter(Boolean).slice(0, 8) : [],
      hold: !!human.hold,
      takeover: !!human.takeover,
      estop: !!human.estop,
      soft_invent: false,
    },
    media: {
      screenshots_n: Number(media.screenshots_n || 0),
      recordings_n: Number(media.recordings_n || 0),
      videos_n: Number(media.videos_n || 0),
      has_screenshot: !!media.has_screenshot,
      has_recording: !!media.has_recording,
      soft_invent: false,
    },
    loop: {
      closed: false,
      production_all_tenants_closed_loop: false,
      leftover_swarm_live: false,
      h5_executed: !!loop.h5_executed,
      h5_arrival: false,
      reason: clip(loop.reason, 48) || "prior_proofs_unsatisfied",
      soft_invent: false,
    },
  };
}

export function surveyMapSvg(place) {
  const p = place && typeof place === "object" ? place : {};
  const ll = asLl(p.ll);
  if (!ll) {
    return `<svg class="folio-map" viewBox="0 0 320 200" role="img" aria-label="Map not surveyed">
      <rect width="320" height="200" fill="#D5DFD4"/>
      <path d="M16 16H304V184H16Z" fill="none" stroke="#2C4A3E" stroke-width="1.2"/>
      <path d="M32 40h256M32 80h256M32 120h256M32 160h256" stroke="#C4B8A4" stroke-width=".6"/>
      <text x="160" y="96" text-anchor="middle" fill="#5C665F" font-size="13" font-family="Bahnschrift, Segoe UI, sans-serif">MAP NOT SURVEYED</text>
      <text x="160" y="118" text-anchor="middle" fill="#8C2F1F" font-size="10" font-family="Cambria, Georgia, serif">${esc(p.skip_reason || "not_surveyed")}</text>
    </svg>`;
  }
  const [lng, lat] = ll;
  const x = 28 + ((lng + 180) / 360) * 264;
  const y = 24 + ((90 - lat) / 180) * 152;
  return `<svg class="folio-map" viewBox="0 0 320 200" role="img" aria-label="Survey plate">
    <rect width="320" height="200" fill="#D5DFD4"/>
    <path d="M16 16H304V184H16Z" fill="none" stroke="#2C4A3E" stroke-width="1.4"/>
    <path d="M32 48h256M32 80h256M32 112h256M32 144h256" stroke="#B7C4B4" stroke-width=".7"/>
    <circle cx="278" cy="36" r="14" fill="none" stroke="#2C4A3E" stroke-width="1"/>
    <path d="M278 22v28M264 36h28" stroke="#2C4A3E" stroke-width="1"/>
    <text x="278" y="18" text-anchor="middle" font-size="8" fill="#2C4A3E">N</text>
    <circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="6" fill="#B56A3C"/>
    <circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="11" fill="none" stroke="#B56A3C" stroke-width="1.2"/>
    <text x="24" y="192" font-size="9" fill="#1C241F" font-family="Consolas, monospace">${esc(lat.toFixed(5))}, ${esc(lng.toFixed(5))} · ${esc(p.ll_source || "device_survey")}</text>
  </svg>`;
}

export function folioFacts(index, extras = {}) {
  const idx = index && typeof index === "object" ? index : {};
  const d = cleanDossier(idx.dossier);
  const localFiles = Array.isArray(extras.local_files) ? extras.local_files.map((x) => clip(x, 80)).filter(Boolean) : [];
  const embeds = Array.isArray(extras.embeds) ? extras.embeds : [];
  return { idx, d, localFiles, embeds };
}

export function folioSheetRows(index, extras = {}) {
  const { idx, d, localFiles } = folioFacts(index, extras);
  const rows = [
    ["section", "field", "value"],
    ["identity", "event_id", d.event_id || idx.event_id || ""],
    ["identity", "team_run_id", d.team_run_id || idx.team_run_id || ""],
    ["identity", "opened_at", d.opened_at],
    ["identity", "device_id", d.device_id],
    ["place", "address", d.place.address],
    ["place", "site_id", d.place.site_id],
    ["place", "zone_id", d.place.zone_id],
    ["place", "ll", d.place.ll ? d.place.ll.join(",") : ""],
    ["place", "map_available", d.place.map_available ? "yes" : "no"],
    ["place", "skip_reason", d.place.skip_reason],
    ["ai", "intervened", d.ai.intervened ? "yes" : "no"],
    ["ai", "live_brain", d.ai.live_brain ? "yes" : "no"],
    ["ai", "brain_honesty", d.ai.brain_honesty],
    ["ai", "intents_source", d.ai.intents_source],
    ["ai", "model_caps", d.ai.model_caps.join(", ")],
    ["human", "intervened", d.human.intervened ? "yes" : "no"],
    ["human", "last_control_verb", d.human.last_control_verb],
    ["human", "hold", d.human.hold ? "yes" : "no"],
    ["human", "takeover", d.human.takeover ? "yes" : "no"],
    ["human", "estop", d.human.estop ? "yes" : "no"],
    ["media", "screenshots_n", String(d.media.screenshots_n)],
    ["media", "recordings_n", String(d.media.recordings_n)],
    ["media", "local_files", localFiles.join(", ")],
    ["loop", "closed", "false"],
    ["loop", "production_all_tenants_closed_loop", "false"],
    ["loop", "h5_arrival", "false"],
    ["loop", "h5_executed", d.loop.h5_executed ? "yes" : "no"],
    ["loop", "reason", d.loop.reason],
  ];
  d.dispatches.forEach((row, i) => {
    const n = String(i + 1);
    rows.push(["dispatch_" + n, "dispatch_id", row.dispatch_id]);
    rows.push(["dispatch_" + n, "status", row.status]);
    rows.push(["dispatch_" + n, "target", row.target]);
    rows.push(["dispatch_" + n, "dispatched_at", row.dispatched_at]);
    rows.push(["dispatch_" + n, "accepted_at", row.accepted_at]);
    rows.push(["dispatch_" + n, "responded", row.responded ? "yes" : "no"]);
    rows.push(["dispatch_" + n, "response_seconds", row.response_seconds == null ? "" : String(row.response_seconds)]);
    rows.push(["dispatch_" + n, "arrived", "false"]);
  });
  return rows;
}

export function buildHtmlFolio(index, extras = {}) {
  const { idx, d, localFiles, embeds } = folioFacts(index, extras);
  const eventId = d.event_id || idx.event_id || "event";
  const shots = embeds.filter((x) => x && x.dataUri && (
    /shot|image|still/i.test(String(x.kind || "")) || /image|jpg|png|jpeg/i.test(x.mime || "")
  ));
  const videos = localFiles.filter((n) => /\.(webm|mp4|mov)$/i.test(n));
  const stamp = d.loop.closed ? "CLOSED" : "OPEN LOOP";
  const aiTone = d.ai.live_brain ? "live" : d.ai.intervened ? "rails" : "none";
  const mediaStrip = shots.slice(0, 8).map((img) => (
    `<figure class="plate"><img src="${esc(img.dataUri)}" alt="${esc(img.name || "still")}"/><figcaption>${esc(img.name || "still")}</figcaption></figure>`
  )).join("");
  const dispatchRows = d.dispatches.length
    ? d.dispatches.map((row) => `<tr>
        <td><code>${esc(row.dispatch_id)}</code></td>
        <td>${esc(dash(row.dispatched_at))}</td>
        <td>${esc(dash(row.target))}</td>
        <td>${esc(row.status || "—")}</td>
        <td>${yesNo(row.responded, "Yes / 已响应", "No / 未响应")}</td>
        <td>${esc(secondsLabel(row.response_seconds))}</td>
        <td>No · listed only</td>
      </tr>`).join("")
    : `<tr><td colspan="7">No dispatch on this event. 此案例没有派遣记录。</td></tr>`;
  return `<!DOCTYPE html>
<html lang="zh-Hans">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>Case folio ${esc(eventId)}</title>
<style>
  :root{--paper:#EFE6D4;--ink:#1C241F;--spine:#2C4A3E;--copper:#B56A3C;--plate:#D5DFD4;--stamp:#8C2F1F;--rule:#C4B8A4;--muted:#5C665F}
  *{box-sizing:border-box}
  body{margin:0;background:#C9BFA8;color:var(--ink);font:16px/1.45 Cambria,Charter,Georgia,serif}
  .jacket{max-width:980px;margin:28px auto;background:var(--paper);display:grid;grid-template-columns:46px 1fr;min-height:100vh;box-shadow:0 18px 50px rgba(28,36,31,.28)}
  .spine{background:var(--spine);color:#EFE6D4;display:flex;align-items:center;justify-content:center;padding:24px 0}
  .spine b{writing-mode:vertical-rl;transform:rotate(180deg);letter-spacing:.22em;font:700 13px/1 Bahnschrift,"Segoe UI Condensed",sans-serif}
  main{padding:28px 34px 40px}
  .mast{display:grid;grid-template-columns:1fr 120px;gap:16px;align-items:start;border-bottom:2px solid var(--ink);padding-bottom:16px}
  .eyebrow{margin:0;font:700 11px/1 Bahnschrift,"Segoe UI Condensed",sans-serif;letter-spacing:.18em;text-transform:uppercase;color:var(--copper)}
  h1{margin:6px 0 0;font:700 34px/.95 Bahnschrift,"Segoe UI Condensed",sans-serif;letter-spacing:-.03em}
  .sub{margin:8px 0 0;color:var(--muted);font-size:14px}
  .stamp{width:110px;height:110px;border:3px solid var(--stamp);border-radius:50%;display:flex;flex-direction:column;align-items:center;justify-content:center;color:var(--stamp);transform:rotate(-8deg)}
  .stamp strong{font:800 13px/1 Bahnschrift,sans-serif;letter-spacing:.08em}
  .stamp small{font:12px/1.2 Cambria,serif;margin-top:4px;text-align:center}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:22px}
  section{border:1px solid var(--rule);padding:14px 16px 16px;background:rgba(255,255,255,.28)}
  section h2{margin:0 0 10px;font:700 13px/1 Bahnschrift,sans-serif;letter-spacing:.14em;text-transform:uppercase;color:var(--spine)}
  dl{display:grid;grid-template-columns:92px 1fr;gap:6px 10px;margin:0}
  dt{color:var(--muted);font:700 11px/1.3 Bahnschrift,sans-serif;letter-spacing:.06em;text-transform:uppercase}
  dd{margin:0}
  .map-wrap{background:var(--plate);padding:8px}
  .folio-map{width:100%;height:auto;display:block}
  table{width:100%;border-collapse:collapse;font-size:13px}
  th,td{border-bottom:1px solid var(--rule);text-align:left;padding:7px 6px;vertical-align:top}
  th{font:700 10px/1 Bahnschrift,sans-serif;letter-spacing:.08em;text-transform:uppercase;color:var(--muted)}
  code{font:12px/1.3 Consolas,"Cascadia Mono",monospace}
  .media{display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:10px}
  .plate{margin:0;background:#1C241F}
  .plate img{width:100%;height:120px;object-fit:cover;display:block}
  .plate figcaption{color:#EFE6D4;font:11px/1.3 Consolas,monospace;padding:6px 8px}
  .tone-live{box-shadow:inset 4px 0 0 #1F6F64}
  .tone-rails{box-shadow:inset 4px 0 0 #C9A227}
  .tone-none{box-shadow:inset 4px 0 0 #8A8376}
  footer{margin-top:22px;padding-top:12px;border-top:2px solid var(--ink);font-size:13px;color:var(--muted)}
  @media print{body{background:white}.jacket{margin:0;box-shadow:none}}
</style>
</head>
<body>
<article class="jacket">
  <aside class="spine"><b>${esc(eventId)}</b></aside>
  <main>
    <header class="mast">
      <div>
        <p class="eyebrow">Croc Sentinel · Case folio / 案例卷宗</p>
        <h1>${esc(eventId)}</h1>
        <p class="sub">IDs on the server. This file stays on this computer. 服务端只留编号，卷宗只在本机。</p>
        <p class="sub">Opened ${esc(dash(d.opened_at))} · Device ${esc(dash(d.device_id))} · Run ${esc(dash(d.team_run_id, "none"))}</p>
      </div>
      <div class="stamp" aria-label="Loop honesty">
        <strong>${esc(stamp)}</strong>
        <small>${esc(d.loop.reason)}</small>
      </div>
    </header>
    <div class="grid">
      <section>
        <h2>Place / 地址</h2>
        <dl>
          <dt>Address</dt><dd>${esc(dash(d.place.address, "Not surveyed / 未勘测"))}</dd>
          <dt>Site</dt><dd>${esc(dash(d.place.site_id))}</dd>
          <dt>Zone</dt><dd>${esc(dash(d.place.zone_id))}</dd>
          <dt>Map</dt><dd>${d.place.map_available ? "Survey plate only · 仅勘测坐标" : esc(d.place.skip_reason || "not_surveyed")}</dd>
        </dl>
      </section>
      <section>
        <h2>Map / 地图</h2>
        <div class="map-wrap">${surveyMapSvg(d.place)}</div>
      </section>
      <section class="tone-${aiTone}">
        <h2>AI / 人工智能介入</h2>
        <dl>
          <dt>Intervened</dt><dd>${yesNo(d.ai.intervened, "Yes / 有", "No / 无")}</dd>
          <dt>Live brain</dt><dd>${yesNo(d.ai.live_brain, "Coordinator", "No · " + dash(d.ai.brain_honesty, "never dialed"))}</dd>
          <dt>Source</dt><dd>${esc(dash(d.ai.intents_source))}</dd>
          <dt>Caps</dt><dd>${esc(d.ai.model_caps.join(", ") || "—")}</dd>
          <dt>Model</dt><dd>${esc(dash(d.ai.effective_model_id))}</dd>
        </dl>
      </section>
      <section>
        <h2>Human / 人类介入</h2>
        <dl>
          <dt>Intervened</dt><dd>${yesNo(d.human.intervened, "Yes / 有", "No / 无")}</dd>
          <dt>Verb</dt><dd>${esc(dash(d.human.last_control_verb))}</dd>
          <dt>Hold</dt><dd>${yesNo(d.human.hold, "Yes", "No")}</dd>
          <dt>Takeover</dt><dd>${yesNo(d.human.takeover, "Yes", "No")}</dd>
          <dt>E-Stop</dt><dd>${yesNo(d.human.estop, "Yes", "No")}</dd>
        </dl>
      </section>
    </div>
    <section style="margin-top:16px">
      <h2>Dispatch / 派遣</h2>
      <table>
        <thead><tr><th>ID</th><th>Time</th><th>Target / 人员</th><th>Status</th><th>Responded</th><th>Wait</th><th>Arrival</th></tr></thead>
        <tbody>${dispatchRows}</tbody>
      </table>
    </section>
    <section style="margin-top:16px">
      <h2>Media / 录像与截图</h2>
      <p class="sub">Server ${d.media.screenshots_n} stills · ${d.media.recordings_n} recordings. Local files: ${esc(localFiles.join(", ") || "none on this computer")}.</p>
      <div class="media">${mediaStrip || "<p>No stills embedded. Video stays as sibling files. 没有可嵌入的截图；录像保存在同级文件夹。</p>"}</div>
      ${videos.length ? `<p class="sub">Video files / 录像：${esc(videos.join(", "))}</p>` : ""}
    </section>
    <footer>
      Loop closed: no. Fleet close: no. H5 arrival: no walking-human proof.
      闭环未成立 · 全租户闭环为假 · 到达证明未满足。 soft_invent=false · b2_upload=false
    </footer>
  </main>
</article>
</body>
</html>`;
}

function utf8(text) {
  return new TextEncoder().encode(String(text));
}

function pdfEscape(text) {
  return String(text)
    .replace(/[^\x20-\x7E]/g, "?")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function pdfRect(x, y, w, h, r, g, b) {
  return `${r} ${g} ${b} rg ${x} ${y} ${w} ${h} re f`;
}

function pdfStroke(x, y, w, h) {
  return `0.11 0.14 0.12 RG 1.2 w ${x} ${y} ${w} ${h} re S`;
}

function pdfText(x, y, size, text, r = 0.11, g = 0.14, b = 0.12) {
  return `${r} ${g} ${b} rg BT /F1 ${size} Tf ${x} ${y} Td (${pdfEscape(text)}) Tj ET`;
}

export function buildFolioPdfBytes(index, extras = {}) {
  const { idx, d, localFiles } = folioFacts(index, extras);
  const eventId = d.event_id || idx.event_id || "event";
  const ll = d.place.ll;
  const mapCmds = ll
    ? [
        pdfRect(318, 430, 230, 150, 0.84, 0.87, 0.83),
        pdfStroke(318, 430, 230, 150),
        `0.71 0.42 0.24 rg ${318 + ((ll[0] + 180) / 360) * 210} ${430 + ((ll[1] + 90) / 180) * 120} 5 5 re f`,
        pdfText(326, 438, 8, `${ll[1].toFixed(5)}, ${ll[0].toFixed(5)} ${d.place.ll_source}`),
      ]
    : [
        pdfRect(318, 430, 230, 150, 0.84, 0.87, 0.83),
        pdfStroke(318, 430, 230, 150),
        pdfText(360, 500, 11, "MAP NOT SURVEYED"),
        pdfText(348, 482, 9, d.place.skip_reason || "not_surveyed", 0.55, 0.18, 0.12),
      ];
  const d0 = d.dispatches[0];
  const cmds = [
    pdfRect(0, 0, 36, 792, 0.17, 0.29, 0.24),
    pdfRect(36, 742, 576, 50, 0.94, 0.90, 0.83),
    pdfText(52, 768, 9, "CROC SENTINEL  CASE FOLIO", 0.71, 0.42, 0.24),
    pdfText(52, 748, 18, eventId),
    pdfRect(500, 748, 86, 36, 0.94, 0.90, 0.83),
    pdfStroke(500, 748, 86, 36),
    pdfText(512, 762, 8, "OPEN LOOP", 0.55, 0.18, 0.12),
    pdfText(508, 750, 7, d.loop.reason, 0.55, 0.18, 0.12),
    pdfRect(52, 430, 250, 150, 0.98, 0.96, 0.91),
    pdfStroke(52, 430, 250, 150),
    pdfText(62, 560, 10, "PLACE / ADDRESS"),
    pdfText(62, 540, 10, `Address: ${d.place.address || "not surveyed"}`),
    pdfText(62, 524, 10, `Site: ${d.place.site_id || "-"}   Zone: ${d.place.zone_id || "-"}`),
    pdfText(62, 508, 10, `Device: ${d.device_id || "-"}`),
    pdfText(62, 492, 10, `Opened: ${d.opened_at || "-"}`),
    ...mapCmds,
    pdfRect(52, 250, 250, 160, 0.98, 0.96, 0.91),
    pdfStroke(52, 250, 250, 160),
    pdfText(62, 390, 10, "AI INTERVENTION"),
    pdfText(62, 370, 10, `Intervened: ${d.ai.intervened ? "yes" : "no"}`),
    pdfText(62, 354, 10, `Live brain: ${d.ai.live_brain ? "coordinator" : "no"}`),
    pdfText(62, 338, 10, `Honesty: ${d.ai.brain_honesty || "-"}`),
    pdfText(62, 322, 10, `Source: ${d.ai.intents_source || "-"}`),
    pdfText(62, 306, 10, `Caps: ${(d.ai.model_caps.join(", ") || "-").slice(0, 42)}`),
    pdfRect(318, 250, 230, 160, 0.98, 0.96, 0.91),
    pdfStroke(318, 250, 230, 160),
    pdfText(328, 390, 10, "HUMAN INTERVENTION"),
    pdfText(328, 370, 10, `Intervened: ${d.human.intervened ? "yes" : "no"}`),
    pdfText(328, 354, 10, `Verb: ${d.human.last_control_verb || "-"}`),
    pdfText(328, 338, 10, `Hold: ${d.human.hold ? "yes" : "no"}`),
    pdfText(328, 322, 10, `Takeover: ${d.human.takeover ? "yes" : "no"}`),
    pdfText(328, 306, 10, `E-Stop: ${d.human.estop ? "yes" : "no"}`),
    pdfRect(52, 88, 496, 146, 0.98, 0.96, 0.91),
    pdfStroke(52, 88, 496, 146),
    pdfText(62, 214, 10, "DISPATCH"),
    pdfText(62, 196, 9, d0
      ? `${d0.dispatch_id}  ${d0.dispatched_at || "-"}  ${d0.target || "unassigned"}`
      : "No dispatch on this event"),
    pdfText(62, 180, 9, d0
      ? `Responded: ${d0.responded ? "yes" : "no"}   Wait: ${secondsLabel(d0.response_seconds)}   Arrival: no`
      : "Responded: -"),
    pdfText(62, 164, 9, `Dispatches: ${d.dispatches.length}   More rows live in the HTML folio`),
    pdfText(62, 140, 9, `Media stills ${d.media.screenshots_n}  recordings ${d.media.recordings_n}`),
    pdfText(62, 124, 9, `Local files: ${(localFiles.join(", ") || "none").slice(0, 70)}`),
    pdfText(52, 64, 8, "Loop closed: no. Fleet close: no. H5 arrival: no. soft_invent=false. Server IDs only."),
    pdfText(52, 50, 8, "Designed HTML folio is the bilingual layout. This PDF is the print companion."),
  ];
  const stream = cmds.join("\n");
  const objs = [
    "1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj",
    "2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj",
    "3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >> endobj",
    `4 0 obj << /Length ${utf8(stream).length} >> stream\n${stream}\nendstream endobj`,
    "5 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj",
  ];
  let body = "%PDF-1.4\n";
  const offsets = [0];
  for (const obj of objs) {
    offsets.push(utf8(body).length);
    body += `${obj}\n`;
  }
  const xrefAt = utf8(body).length;
  let xref = `xref\n0 6\n0000000000 65535 f \n`;
  for (let i = 1; i <= 5; i++) {
    xref += `${String(offsets[i]).padStart(10, "0")} 00000 n \n`;
  }
  body += `${xref}trailer << /Size 6 /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF\n`;
  return utf8(body);
}
